import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, User, KeyRound } from "lucide-react";

function CreateUserCard({ api, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${api}/auth/users`, { email, password, name: name || null });
      toast.success(`Account created for ${email}`);
      setEmail(""); setPassword(""); setName("");
      onCreated();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create user");
    } finally { setCreating(false); }
  };

  return (
    <Card className="lg:col-span-1 border-slate-200 shadow-sm h-fit">
      <CardHeader className="border-b border-slate-100">
        <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Admin</div>
        <CardTitle className="text-lg">Create Staff Account</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={create} className="space-y-4" data-testid="create-user-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Email *</Label>
            <Input type="email" required className="mt-2 bg-white" placeholder="staff@workplaceone.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name (optional)</Label>
            <Input className="mt-2 bg-white" placeholder="Jane Smith" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Temporary Password *</Label>
            <Input type="text" required minLength={8} className="mt-2 bg-white" placeholder="Min 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-user-password-input" />
          </div>
          <Button type="submit" disabled={creating} className="w-full bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="create-user-button">
            <UserPlus className="w-4 h-4 mr-2" />{creating ? "Creating..." : "Create Account"}
          </Button>
          <p className="text-xs text-slate-500">@workplaceone.com and @terrierproperties.ca emails are accepted. Staff can submit requests only — they cannot see the dashboard, stats, or reports.</p>
        </form>
      </CardContent>
    </Card>
  );
}

function UserRow({ u, currentUser, onReset, onRemove }) {
  const isAdminRow = u.role === "admin";
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
      <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
      <td className="px-4 py-3">{u.name || "—"}</td>
      <td className="px-4 py-3">
        {isAdminRow ? (
          <Badge className="border bg-slate-900 text-white border-slate-900"><ShieldCheck className="w-3 h-3 mr-1" />Admin</Badge>
        ) : (
          <Badge className="border bg-slate-100 text-slate-700 border-slate-200"><User className="w-3 h-3 mr-1" />Staff</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <Button variant="ghost" size="sm" onClick={() => onReset(u)} className="text-slate-400 hover:text-amber-600" title="Set new password" data-testid={`reset-password-${u.email}`}>
          <KeyRound className="w-4 h-4" />
        </Button>
        {!isAdminRow && u.email !== currentUser?.email && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(u.id, u.email)} className="text-slate-400 hover:text-red-600" data-testid={`delete-user-${u.email}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function UsersPanel({ api, currentUser }) {
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${api}/auth/users`); setUsers(r.data); } catch (e) { toast.error("Failed to load users"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id, em) => {
    if (!window.confirm(`Remove account ${em}?`)) return;
    try {
      await axios.delete(`${api}/auth/users/${id}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to remove");
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email} (min 8 characters):`);
    if (pw === null) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await axios.put(`${api}/auth/users/${u.id}/password`, { password: pw });
      toast.success(`Password updated for ${u.email}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to update password");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <CreateUserCard api={api} onCreated={load} />

      <Card className="lg:col-span-2 border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Accounts</div>
          <CardTitle className="text-lg">All Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow key={u.id} u={u} currentUser={currentUser} onReset={resetPassword} onRemove={remove} />
              ))}
              {users.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
